import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, Calculator, Package, Home, ClipboardList, Landmark, Inbox, FolderOpen, KanbanSquare, Mail, Sparkles, Star, CreditCard, FileSignature, Wrench, ShieldCheck, Camera, Server, Cable, ListChecks, Clock, GitPullRequestArrow, Building2, Repeat, Receipt, PhoneCall, Users, MessageSquareQuote, Megaphone, Images, Network, Search } from "lucide-react";

export const Route = createFileRoute("/tools/")({
  head: () => ({
    meta: [
      { title: "Admin Tools | Apex AV" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminIndex,
});

const categories = [
  {
    id: "sales",
    label: "Sales & Estimates",
    blurb: "Quotes, pricing, and the client-ready documents that close the job.",
    tools: [
      {
        to: "/estimator" as const,
        title: "Project Estimator",
        description: "Build client-ready project estimates and PDFs.",
        icon: ClipboardList,
      },
      {
        to: "/tools/estimates" as const,
        title: "Saved Estimates",
        description: "Browse, search, and reopen saved client estimates.",
        icon: FolderOpen,
      },
      {
        to: "/blueprint-counter" as const,
        title: "Blueprint Counter",
        description: "Count and estimate icons from uploaded blueprints.",
        icon: Calculator,
      },
      {
        to: "/tools/proposals" as const,
        title: "Proposals",
        description: "Client-ready proposals with line items, terms, and signature block.",
        icon: FileSignature,
      },
      {
        to: "/tools/products" as const,
        title: "Products",
        description: "Import your dealer price file and pull products into proposals and estimates.",
        icon: Package,
      },
      {
        to: "/tools/cable-estimator" as const,
        title: "Cable Estimator",
        description: "Estimate cable footage and cost by run with waste factor.",
        icon: Cable,
      },
      {
        to: "/tools/rack-layout" as const,
        title: "Rack Layout Designer",
        description: "Visual RU-accurate rack builder with printable layouts.",
        icon: Server,
      },
    ],
  },
  {
    id: "projects",
    label: "Projects & Fieldwork",
    blurb: "Day-to-day job tracking, from walkthrough to punch list and sign-off.",
    tools: [
      {
        to: "/tools/projects" as const,
        title: "Project Tracker",
        description: "Kanban board for jobs with scheduling and day-before reminders.",
        icon: KanbanSquare,
      },
      {
        to: "/tools/site-surveys" as const,
        title: "Site Surveys",
        description: "Pre-install walkthroughs with rooms, notes, and photos.",
        icon: Building2,
      },
      {
        to: "/tools/checklists" as const,
        title: "Install Checklists",
        description: "Reusable templates and per-job installation checklists.",
        icon: ListChecks,
      },
      {
        to: "/tools/change-orders" as const,
        title: "Change Orders",
        description: "Post-signature scope changes with line items and signature block.",
        icon: GitPullRequestArrow,
      },
      {
        to: "/tools/timesheets" as const,
        title: "Timesheets",
        description: "Clock in/out per job with CSV export by employee and date.",
        icon: Clock,
      },
      {
        to: "/tools/photos" as const,
        title: "Job Site Photos",
        description: "Photo log for installs and service. Snap from phone or upload.",
        icon: Camera,
      },
      {
        to: "/tools/engineering" as const,
        title: "Engineering Documents",
        description: "System engineering packages: cover sheet, rack elevations, and wired schematics.",
        icon: Network,
      },
      {
        to: "/tools/service-tickets" as const,
        title: "Support Tickets",
        description: "Customer support requests from /support plus service calls, with notes and status emails.",
        icon: Wrench,
      },
      {
        to: "/tools/maintenance" as const,
        title: "Maintenance Agreements",
        description: "Recurring service plans with next-visit tracking.",
        icon: Repeat,
      },
    ],
  },
  {
    id: "customers",
    label: "Customers & Leads",
    blurb: "Who called, what they asked for, and what still needs a follow-up.",
    tools: [
      {
        to: "/tools/clients" as const,
        title: "Customers",
        description: "Shared customer directory linking projects, estimates, photos, and notes.",
        icon: Users,
      },
      {
        to: "/tools/contacts" as const,
        title: "Contact Submissions",
        description: "Review and triage inbound contact form messages.",
        icon: Inbox,
      },
      {
        to: "/tools/follow-ups" as const,
        title: "Follow-Ups",
        description: "Lead & customer nurture tasks with due dates and channels.",
        icon: PhoneCall,
      },
      {
        to: "/tools/referrals" as const,
        title: "Referral Tracker",
        description: "Track who referred new business and reward payouts.",
        icon: Users,
      },
      {
        to: "/sold-homes" as const,
        title: "Sold Homes Search",
        description: "Look up recently sold homes by ZIP code.",
        icon: Home,
      },
      {
        to: "/tools/searches" as const,
        title: "Site Searches",
        description: "See what visitors search for on the website, and what came up empty.",
        icon: Search,
      },
    ],
  },
  {
    id: "marketing",
    label: "Marketing & Content",
    blurb: "Everything that puts the brand in front of the next customer.",
    tools: [
      {
        to: "/tools/blog" as const,
        title: "Blog Posts",
        description: "Create, edit, and publish blog articles.",
        icon: FileText,
      },
      {
        to: "/tools/marketing-copy" as const,
        title: "Ad Writer",
        description: "Draft service ads for Google Search, social, email, and SMS.",
        icon: Megaphone,
      },
      {
        to: "/tools/reviews" as const,
        title: "Google Reviews",
        description: "Pull your latest Google reviews and display them on the homepage.",
        icon: Star,
      },
      {
        to: "/tools/testimonials" as const,
        title: "Testimonials",
        description: "Curate customer quotes shown on the public site.",
        icon: MessageSquareQuote,
      },
      {
        to: "/tools/brands" as const,
        title: "Brand Manager",
        description: "Upload logos and map brands to their service pages.",
        icon: Sparkles,
      },
      {
        to: "/tools/gallery" as const,
        title: "Image SEO & Gallery",
        description: "Add alt text, titles, and captions to site images and pick what shows on the Gallery page.",
        icon: Images,
      },
      {
        to: "/tools/before-after" as const,
        title: "Before & After Combiner",
        description: "Merge two job photos into one shareable before/after image.",
        icon: Images,
      },
      {
        to: "/tools/business-cards" as const,
        title: "Business Cards",
        description: "Design and print per-employee business cards with a QR code.",
        icon: CreditCard,
      },
      {
        to: "/tools/letter" as const,
        title: "New Homeowner Letter",
        description: "Printable one-page marketing letter with logo, brands, and services.",
        icon: Mail,
      },
    ],
  },
  {
    id: "backoffice",
    label: "Inventory & Back Office",
    blurb: "Stock, warranties, and the numbers behind the business.",
    tools: [
      {
        to: "/inventory" as const,
        title: "Van Inventory",
        description: "Scan and manage van and warehouse inventory.",
        icon: Package,
      },
      {
        to: "/tools/warranty" as const,
        title: "Warranty Tracker",
        description: "Log installed products with serial numbers and warranty expiration.",
        icon: ShieldCheck,
      },
      {
        to: "/tools/expenses" as const,
        title: "Expenses & Mileage",
        description: "Log expenses and mileage with receipts and CSV export.",
        icon: Receipt,
      },
      {
        to: "/tools/bank-statements" as const,
        title: "Bank Statement Analyzer",
        description: "Upload PDFs to get yearly deposit and withdrawal totals.",
        icon: Landmark,
      },
    ],
  },
];

function AdminIndex() {
  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-foreground">Tools</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Internal tools and admin utilities, grouped by what you're working on. All links below are
        behind admin auth.
      </p>

      <nav aria-label="Tool categories" className="mt-6 flex flex-wrap gap-2">
        {categories.map((category, index) => (
          <a
            key={category.id}
            href={`#${category.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-accent hover:text-foreground"
          >
            <span className="tabular-nums text-brand-text">
              {String(index + 1).padStart(2, "0")}
            </span>
            {category.label}
            <span className="tabular-nums text-muted-foreground/70">{category.tools.length}</span>
          </a>
        ))}
      </nav>

      {categories.map((category, index) => (
        <section key={category.id} id={category.id} className="mt-14 scroll-mt-24">
          <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b border-border pb-4">
            <div>
              <div className="eyebrow text-brand-text">
                {String(index + 1).padStart(2, "0")}
              </div>
              <h2 className="mt-2 font-display text-2xl font-semibold text-foreground">
                {category.label}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{category.blurb}</p>
            </div>
            <div className="eyebrow text-muted-foreground/70">
              {category.tools.length} {category.tools.length === 1 ? "tool" : "tools"}
            </div>
          </header>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {category.tools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.to}
                  to={tool.to}
                  className="group rounded-lg border border-border bg-card p-5 transition-colors hover:border-foreground/30 hover:bg-accent"
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-md bg-gradient-brand p-2 text-brand-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground">
                      {tool.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{tool.description}</p>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
